import { TFunction } from "i18next";

import {
  IReportGridColumn,
  normalizeReportGridValue,
  ReportPeriodGroupingEnum,
  TimeZoneEnum,
} from "@ryde/shared";

import { REPORT_CHART_GROUPING_DISPLAY_SEPARATOR } from "./report-chart.constants";
import { truncateLabel } from "./report-chart.utils";
import { parseReportGroupingCompositeKey } from "./report-chart.utils.data";

export const formatReportChartDimensionPart = ({
  rawValue,
  column,
  periodGrouping,
  timezone,
  t,
  formatDateByGroupingPeriod,
}: {
  rawValue: string;
  column: IReportGridColumn | undefined;
  periodGrouping?: ReportPeriodGroupingEnum;
  timezone: TimeZoneEnum;
  t: TFunction;
  formatDateByGroupingPeriod: (
    date: TDateISO,
    period?: ReportPeriodGroupingEnum,
  ) => string;
}): string => {
  if (!rawValue) {
    return "";
  }

  if (column?.isPeriodGroupingColumn) {
    const period = column.periodGrouping ?? periodGrouping;

    return formatDateByGroupingPeriod(rawValue as TDateISO, period);
  }

  const normalized = normalizeReportGridValue({
    value: rawValue,
    reportGridColumn: column,
    timezone,
    t,
  });

  return String(normalized ?? rawValue);
};

export const formatReportGroupingLabel = ({
  compositeKey,
  groupingColumns,
  periodGrouping,
  timezone,
  t,
  formatDateByGroupingPeriod,
  truncate = true,
}: {
  compositeKey: string;
  groupingColumns: IReportGridColumn[];
  periodGrouping?: ReportPeriodGroupingEnum;
  timezone: TimeZoneEnum;
  t: TFunction;
  formatDateByGroupingPeriod: (
    date: TDateISO,
    period?: ReportPeriodGroupingEnum,
  ) => string;
  truncate?: boolean;
}): string => {
  const rawParts = parseReportGroupingCompositeKey(compositeKey);

  const formattedParts = rawParts
    .map((rawPart, index) =>
      formatReportChartDimensionPart({
        rawValue: rawPart,
        column: groupingColumns[index],
        periodGrouping,
        timezone,
        t,
        formatDateByGroupingPeriod,
      }),
    )
    .filter(part => part.length > 0);

  const combined = formattedParts.join(REPORT_CHART_GROUPING_DISPLAY_SEPARATOR);

  return truncate ? truncateLabel({ text: combined }) : combined;
};
